import React, { useEffect, useRef, useState } from "react";
import { ImageIcon } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  createCredentialPhotoPreviewUrl,
  isHeicCredentialPhoto,
  revokeCredentialPhotoPreviewUrl,
} from "../../../lib/credentialPhotoPreview";

type CredentialPhotoDropzoneProps = {
  disabled?: boolean;
  required?: boolean;
};

export const CredentialPhotoDropzone: React.FC<
  CredentialPhotoDropzoneProps
> = ({ disabled = false, required = true }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewUrlRef = useRef<string | null>(null);
  const previewRequestIdRef = useRef(0);

  const clearPreviewUrl = () => {
    revokeCredentialPhotoPreviewUrl(previewUrlRef.current);
    previewUrlRef.current = null;
    setPreviewUrl(null);
  };

  useEffect(() => {
    return () => {
      revokeCredentialPhotoPreviewUrl(previewUrlRef.current);
      previewUrlRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!selectedFile) {
      clearPreviewUrl();
      setPreviewFailed(false);
      setPreviewLoading(false);
      return;
    }

    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setPreviewLoading(true);
    setPreviewFailed(false);
    clearPreviewUrl();

    void createCredentialPhotoPreviewUrl(selectedFile)
      .then((url) => {
        if (previewRequestIdRef.current !== requestId) {
          revokeCredentialPhotoPreviewUrl(url);
          return;
        }
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (previewRequestIdRef.current !== requestId) return;
        setPreviewFailed(true);
      })
      .finally(() => {
        if (previewRequestIdRef.current === requestId) {
          setPreviewLoading(false);
        }
      });
  }, [selectedFile]);

  const hasPreviewState = Boolean(
    selectedFile && (previewUrl || previewLoading || previewFailed),
  );

  return (
    <label
      className={cn(
        "relative flex min-h-64 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-premium-200 bg-premium-50/40 text-center transition-colors hover:bg-premium-50",
        disabled && "pointer-events-none opacity-60",
        hasPreviewState && "border-solid border-border bg-card",
      )}
    >
      {hasPreviewState ? (
        <>
          {previewLoading ? (
            <span className="px-6 py-10 text-sm text-muted-foreground">
              Preparing preview…
            </span>
          ) : previewUrl && !previewFailed ? (
            <img
              src={previewUrl}
              alt="Selected credential photo"
              className="absolute inset-0 h-full w-full object-contain"
              onError={() => setPreviewFailed(true)}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-muted-foreground">
              <ImageIcon className="h-10 w-10" aria-hidden />
              <span className="text-sm font-medium text-foreground">
                {selectedFile?.name ?? "Selected photo"}
              </span>
              <span className="max-w-xs text-xs">
                {selectedFile && isHeicCredentialPhoto(selectedFile)
                  ? "Preview is not available for this format in your browser. The photo will still upload."
                  : "We could not show a preview, but the photo will still upload."}
              </span>
            </div>
          )}
          <span className="absolute inset-x-0 bottom-0 bg-background/80 px-3 py-2 text-sm font-medium text-foreground">
            Replace photo
          </span>
        </>
      ) : (
        <span className="px-6 py-10">
          <span className="block text-lg font-semibold text-foreground">
            Drop a photo or tap to choose
          </span>
          <span className="mt-2 block text-sm text-muted-foreground">
            JPEG, PNG, or HEIC
          </span>
        </span>
      )}
      <input
        className="sr-only"
        type="file"
        name="photo"
        accept="image/*"
        capture="environment"
        required={required}
        disabled={disabled}
        aria-label="Credential photo"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          setSelectedFile(file);
        }}
      />
    </label>
  );
};
